import CloseIcon from '@mui/icons-material/Close';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { getSubscriptionAPI, SubscriptionItem } from '@src/api/subscription';
import { Dialog } from '@src/components/dialog';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';

const PLAN_COLORS: Record<string, { bgcolor: string; color: string }> = {
  DAY: { bgcolor: '#FFF3E0', color: '#E65100' },
  WEEK: { bgcolor: '#EDE8FF', color: '#6A00AC' },
  MONTH: { bgcolor: '#E6F9F0', color: '#00875A' },
};

type InfoFieldProps = {
  label: string;
  value: React.ReactNode;
  sub?: string;
};

const InfoField = ({ label, value, sub }: InfoFieldProps) => (
  <Box>
    <Typography fontSize={10} fontWeight={600} color="#9CA3AF" textTransform="uppercase" letterSpacing={0.8} mb={0.5}>
      {label}
    </Typography>
    <Typography fontSize={14} fontWeight={600} color="#111827">{value}</Typography>
    {sub && <Typography fontSize={11} color="#9CA3AF" mt={0.25}>{sub}</Typography>}
  </Box>
);

type Props = {
  open: boolean;
  onClose: () => void;
  item: SubscriptionItem | null;
  onDeactivate: () => void;
};

const SubscriptionDetailsModal = ({ open, onClose, item, onDeactivate }: Props) => {
  const { data: fetched, isLoading } = useQuery({
    queryKey: ['subscription-detail', item?.id],
    queryFn: () => getSubscriptionAPI(item!.id),
    enabled: open && !!item?.id,
  });

  if (!item) return null;

  const detail = fetched ?? item;
  const planStyle = PLAN_COLORS[detail?.subscription?.name] || { bgcolor: '#F3F4F6', color: '#6B7280' };
  const planLabel = detail?.subscription?.name === 'Weekly VIP' ? 'Weekly' : detail?.subscription?.name === 'DAY' ? 'Daily' : 'Monthly';
  const isActive = detail.status === 'ACTIVE';

  const expiresFromNow = detail.expiresAt
    ? `${moment(detail.expiresAt).diff(moment(detail.startedAt), 'days')} days from transaction`
    : '';

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      maxWidth="sm"
      dividers={false}
      dialogTitle={
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize={16} fontWeight={700} letterSpacing={1} color="#111827">
            SUBSCRIPTION DETAILS
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
      dialogContent={
        <Box sx={{ width: '100%', px: 1 }}>
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <>
              {/* User info */}
              <Box mb={2.5}>
                <Typography fontSize={15} fontWeight={700} color="#111827">
                  {detail.user?.displayName ?? 'Unknown'}
                </Typography>
                <Typography fontSize={13} color="#6B7280">
                  User ID: {detail.user?.id}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              {/* Info grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  rowGap: 2.5,
                  columnGap: 3,
                }}
              >
                <InfoField
                  label="Plan"
                  value={
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography fontSize={14} fontWeight={600} color="#111827">
                        {planLabel} VIP
                      </Typography>
                      <Chip
                        label={planLabel}
                        size="small"
                        sx={{ bgcolor: planStyle.bgcolor, color: planStyle.color, fontWeight: 700, fontSize: 11, borderRadius: '6px' }}
                      />
                    </Stack>
                  }
                />
                <InfoField
                  label="Status"
                  value={
                    <Typography fontSize={14} fontWeight={700} color={isActive ? '#00875A' : '#DE350B'}>
                      {detail.status}
                    </Typography>
                  }
                />
                <InfoField
                  label="Transaction Date"
                  value={detail.startedAt ? moment.parseZone(detail.startedAt).format('MM-DD-YYYY hh:mm A') : '-'}
                />
                <InfoField
                  label="Start Date"
                  value={detail.startedAt ? moment.parseZone(detail.startedAt).format('MM-DD-YYYY hh:mm A') : '-'}
                />
                <InfoField
                  label="Expires On (Auto)"
                  value={detail.expiresAt ? moment.parseZone(detail.expiresAt).format('MM-DD-YYYY hh:mm A') : '-'}
                  sub={expiresFromNow}
                />
                <InfoField
                  label="Amount"
                  value={`€${detail?.subscription?.price}`}
                />
                <InfoField
                  label="Transaction ID"
                  value={detail.id ?? '-'}
                />
                <InfoField
                  label="Last Updated"
                  value={detail.updatedAt ? moment.parseZone(detail.updatedAt).format('MMM DD, YYYY hh:mm A') : '-'}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Actions */}
              <Stack direction="row" gap={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={onClose}
                  sx={{ borderRadius: '8px', height: 44, fontWeight: 700, fontSize: 13 }}
                >
                  CANCEL
                </Button>
                {isActive && (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PowerSettingsNewIcon />}
                    onClick={onDeactivate}
                    sx={{
                      borderRadius: '8px',
                      height: 44,
                      fontWeight: 700,
                      fontSize: 13,
                      bgcolor: '#DE350B',
                      '&:hover': { bgcolor: '#BF2600' },
                    }}
                  >
                    DEACTIVATE SUBSCRIPTION
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Box>
      }
    />
  );
};

export default SubscriptionDetailsModal;
